import { Link } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-indigo-600 text-white py-6">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8" />
            <h1 className="font-heading font-black text-3xl">Privacy Policy</h1>
          </div>
          <p className="text-white/80 mt-2">Effective Date: January 2025</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-3xl border-2 border-slate-100 shadow-sm p-8 md:p-12">
          <p className="text-slate-600 leading-relaxed mb-8">
            ClearPath Testing Solutions / Clear Path Testing Solutions LLC ("we," "us," "our") respects your privacy. This Privacy Policy explains what information we collect, how we use it, and your choices.
          </p>

          <section className="mb-8">
            <h2 className="font-heading font-bold text-xl text-slate-800 mb-4">Information We Collect</h2>
            
            <h3 className="font-bold text-slate-700 mb-2">Information you provide:</h3>
            <ul className="list-disc list-inside text-slate-600 space-y-1 mb-4">
              <li>Name (optional), email, password (if you create an account)</li>
              <li>Support messages you send us</li>
            </ul>

            <h3 className="font-bold text-slate-700 mb-2">Information collected automatically:</h3>
            <ul className="list-disc list-inside text-slate-600 space-y-1 mb-4">
              <li>Device and app usage data (e.g., pages viewed, session time, feature use)</li>
              <li>Log data (IP address, device type, browser/app version)</li>
              <li>Cookies or similar technologies (for website functionality and analytics)</li>
            </ul>

            <h3 className="font-bold text-slate-700 mb-2">Learning data:</h3>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li>Practice results, scores, progress, and skill performance</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-bold text-xl text-slate-800 mb-4">How We Use Information</h2>
            <p className="text-slate-600 mb-3">We use information to:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li>Provide and improve the app (practice, tracking, personalization)</li>
              <li>Maintain security and prevent fraud/abuse</li>
              <li>Respond to support requests</li>
              <li>Send service messages (account, updates). Marketing emails only if you opt in.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-bold text-xl text-slate-800 mb-4">Sharing of Information</h2>
            <p className="text-slate-600 mb-3">
              <strong>We do not sell your personal information.</strong>
            </p>
            <p className="text-slate-600 mb-3">
              We may share limited data with service providers that help us operate (examples):
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li>Hosting and database providers</li>
              <li>Analytics tools</li>
              <li>Payment processors (if subscriptions exist)</li>
            </ul>
            <p className="text-slate-600 mt-3">
              These providers are required to protect your data and use it only to provide services to us.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-bold text-xl text-slate-800 mb-4">Payments</h2>
            <p className="text-slate-600">
              If you purchase Premium, payments are processed by a third-party payment processor (Stripe). We do not store your full payment card details.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-bold text-xl text-slate-800 mb-4">Children's Privacy</h2>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-slate-700">
                ClearPath Testing Solutions is not intended for children under 13 without a parent/guardian or school supervision. If you believe a child under 13 provided personal information without consent, contact us to remove it.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-bold text-xl text-slate-800 mb-4">Data Retention</h2>
            <p className="text-slate-600">
              We keep your account and learning data as long as your account is active or as needed to provide the service. You may request deletion at any time.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-bold text-xl text-slate-800 mb-4">Your Choices & Rights</h2>
            <p className="text-slate-600 mb-3">You can:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li>Update your profile information</li>
              <li>Opt out of marketing emails</li>
              <li>Request access, correction, or deletion of your data by contacting us</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-bold text-xl text-slate-800 mb-4">Security</h2>
            <p className="text-slate-600">
              We use reasonable safeguards to protect your information, but no system is 100% secure.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading font-bold text-xl text-slate-800 mb-4">Changes</h2>
            <p className="text-slate-600">
              We may update this policy. If changes are significant, we'll notify users in the app or on the website.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-slate-800 mb-4">Contact</h2>
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
              <p className="text-slate-700">
                Privacy questions: <a href="mailto:privacy@clearpathtestprep.com" className="text-indigo-600 hover:underline">privacy@clearpathtestprep.com</a>
              </p>
              <p className="text-slate-600 mt-2">
                Clear Path Testing Solutions LLC<br />
                Texas, United States
              </p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-100 py-6 text-center text-slate-500 text-sm">
        © 2025 Clear Path Testing Solutions LLC. All rights reserved.
      </footer>
    </div>
  );
};

export default Privacy;
